import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSearch, FiUsers } from 'react-icons/fi';
import { API_URLS, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite } from '../utilities/adminAccess';
import { champFields, extractList, teamShowEnabled } from '../utilities/tournamentsApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/teamsManagement.css';

export default function TeamsManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();

  const [teams, setTeams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!champId && champsLoading) return undefined;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadTeams(searchQuery);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, champId, champsLoading]);

  const loadTeams = async (search) => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.getCatalogTeams, {
        ...champFields(champId),
        search: search && search.trim() ? search.trim() : undefined,
      });
      setTeams(extractList(response.data));
    } catch (error) {
      setTeams([]);
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      key: 'show',
      label: t('stStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span className={`st-badge ${teamShowEnabled(item) ? 'enabled' : 'disabled'}`}>
          <span className="tr-btn-full">{teamShowEnabled(item) ? t('stEnabled') : t('stDisabled')}</span>
          <span className="tr-btn-short">{teamShowEnabled(item) ? t('stEnabledShort') : t('stDisabledShort')}</span>
        </span>
      ),
    },
    {
      key: 'name',
      label: t('tmListName'),
      className: 'st-cell-name',
      render: (item) => {
        const country = item.country_name || item.country || '';
        return (
          <span className="tr-name tm-team-main">
            <img
              src={getTeamImageUrl(item.image) || defaultTeamLogo}
              alt=""
              className="tr-team-logo"
              onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
            />
            <span className="tm-team-text">
              <span className="tm-team-name">{item.name || item.team_name || t('tmUntitled')}</span>
              {country ? <small className="tm-team-meta">{country}</small> : null}
            </span>
          </span>
        );
      },
    },
    {
      key: 'country_name',
      label: t('championship'),
      className: 'st-cell-muted tm-cell-country',
      render: (item) => item.country_name || item.country || '—',
    },
  ];

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tm-list-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navTeams') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('tmSearchPh')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="st-meta">
            <strong>{t('stFound', { count: teams.length })}</strong>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              <h2 className="st-block-header">{t('navTeams')}</h2>
              {loading ? (
                <div className="st-loading">
                  <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                  <p>{t('tmLoading')}</p>
                </div>
              ) : (
                <ReorderableTable
                  tableClassName="st-table-teams"
                  columns={columns}
                  items={teams}
                  getRowKey={(item) => item.id || item.team_id}
                  reorderMode={false}
                  getRowHref={(item) => {
                    const id = item.id || item.team_id;
                    return id ? `/teams_management/${id}` : null;
                  }}
                  empty={(
                    <div className="st-empty">
                      <FiUsers size={48} />
                      <h3>{t('tmEmpty')}</h3>
                      <p>{t('tmEmptyHint')}</p>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </div>

        <div className="st-actions">
          <IfCanWrite entity="teams">
            <Link
              to="/teams_management/new"
              className={`st-btn st-btn-primary${loading ? ' is-disabled' : ''}`}
              aria-disabled={loading || undefined}
              onClick={(event) => { if (loading) event.preventDefault(); }}
            >
              <FiPlus />
              <span className="tr-btn-full">{t('tmAdd')}</span>
              <span className="tr-btn-short">{t('tmAddShort')}</span>
            </Link>
          </IfCanWrite>
        </div>
      </div>
    </div>
  );
}
